import React, { useEffect, useState } from 'react';
import { FluentProvider, webDarkTheme, webLightTheme } from '@fluentui/react-components';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import Dashboard from '../pages/dashboard/Dashboard';
import Header from '../components/header/Header';
import Navigation from '../components/navigation/Navigation';
import { MsalAuthenticationTemplate, useMsal } from "@azure/msal-react";
import { useAppStyles } from './App.styles';
import Latency from '../pages/latency/latency';
import Reports from '../pages/reports/reports';
import ChatPage from '../pages/chat/ChatPage';
import { BatchDashboard } from '../pages/batch-dashboard';
import { ManualInvoice, isManualInvoiceCreatorEnabled } from '../pages/manual-invoice';
import { FailureAnalysis } from '../pages/failure-analysis';
import CfdiLookup from '../pages/cfdi-lookup/CfdiLookup';
import { InteractionType } from '@azure/msal-browser';
import { jwtDecode } from 'jwt-decode'
import Configuration from "../config/einvoicing-configuration";
import { acquireAccessToken, clearMsalState } from '../utils/authToken';

function AuthLoading() {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <p>Signing in...</p>
    </div>;
}

function AuthError({ error }: { error: any }) {
    const errorCode = error?.errorCode || 'unknown';
    const isRecoverable = errorCode === 'refresh_token_expired'
        || errorCode === 'interaction_required'
        || errorCode === 'login_required'
        || errorCode === 'redirect_bridge_timeout'
        || errorCode === 'interaction_in_progress';

    useEffect(() => {
        if (isRecoverable) {
            clearMsalState();
            window.location.href = '/';
        }
    }, [isRecoverable]);

    if (isRecoverable) {
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
            <p>Redirecting...</p>
        </div>;
    }

    return <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', gap: '16px' }}>
        <p>Sign-in error: {errorCode} — {error?.message || 'Unknown error'}</p>
        <button onClick={() => {
            clearMsalState();
            window.location.href = '/';
        }}>Clear state and retry</button>
    </div>;
}


function App() {

    const [darkTheme, setDarkTheme] = React.useState<boolean>(false);

    const [isAdmin, setIsAdmin] = React.useState<boolean>(false);
    const { instance, accounts } = useMsal();
    const [accessToken, setAccessToken] = useState('');

    // Fix FluentUI Tabster dummy elements that are focusable while aria-hidden,
    // aria-labelledby on divs without a valid role, and aria-label="undefined" on spans
    useEffect(() => {
        const fixA11yIssues = () => {
            // Fix aria-hidden focusable tabster dummies
            document.querySelectorAll<HTMLElement>('[data-tabster-dummy][aria-hidden="true"][tabindex="0"]').forEach(el => {
                el.setAttribute('tabindex', '-1');
            });

            // Fix aria-labelledby on divs with missing or invalid role (Fluent UI Tooltip side-effect)
            document.querySelectorAll<HTMLElement>('div[aria-labelledby]').forEach(el => {
                const role = el.getAttribute('role');
                if (!role || role.trim() === '') {
                    el.removeAttribute('aria-labelledby');
                }
            });

            // Fix aria-label="undefined" on spans without a valid role
            document.querySelectorAll<HTMLElement>('span[aria-label="undefined"]').forEach(el => {
                el.removeAttribute('aria-label');
            });
        };
        const observer = new MutationObserver(() => {
            observer.disconnect();
            fixA11yIssues();
            observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['tabindex', 'aria-hidden', 'aria-labelledby', 'aria-label'] });
        });
        observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['tabindex', 'aria-hidden', 'aria-labelledby', 'aria-label'] });
        fixA11yIssues();
        return () => observer.disconnect();
    }, []);

    async function getAccessToken() {
        if (accessToken === '') {
            const token = await acquireAccessToken(instance, accounts[0]);
            setAccessToken(token);
            return token;
        }
        return accessToken;
    }

    function useAdminCheck() {
        useEffect(() => {
            async function startLatency() {
                if (!ignore) {
                    try {
                        const token = await getAccessToken();
                        if (token === '') {
                            // An interactive sign-in/redirect is in progress; skip
                            // the admin check until a real token is available.
                            setIsAdmin(false);
                            return;
                        }
                        const decodedToken: any = jwtDecode(token);
                        const roles: string[] = decodedToken['roles'];
                        if (roles.includes('EInvoicingDashboard.Admin')) {
                            setIsAdmin(true);
                        }
                        else {
                            setIsAdmin(false);
                        }
                    }
                    catch {
                    }
                }
            }

            let ignore = false;
            startLatency();
            return () => {
                ignore = true;
            }
        }, []);
    }

    useAdminCheck();

    const appStyles = useAppStyles();

    const manualInvoiceCreatorEnabled = isManualInvoiceCreatorEnabled(Configuration);

    return (
        <FluentProvider theme={darkTheme ? webDarkTheme : webLightTheme}>
            <MsalAuthenticationTemplate
                interactionType={InteractionType.Redirect}
                authenticationRequest={Configuration.msalTokenRequest}
                loadingComponent={AuthLoading}
                errorComponent={AuthError}
            >
                <div className={appStyles.primaryContainer} dir="ltr" lang="en">
                    <a href="#main" className="skip-nav-link" onClick={(e) => { e.preventDefault(); const main = document.getElementById('main'); if (main) { main.focus(); main.scrollIntoView(); } }}>Skip to main content</a>
                    <Header setDarkTheme={setDarkTheme} darkTheme={darkTheme} />
                    <BrowserRouter>
                        <div style={{ display: 'flex', flexGrow: 1, minHeight: 'calc(100vh - 3rem)' }}>
                            <Navigation isAdmin={isAdmin}/>
                            <main id='main' aria-label="Primary content" tabIndex={-1} className={appStyles.root} style={{ overflow: 'auto', width: '100%', marginLeft: 0 }}>
                                <Routes>
                                    <Route path="/" element={<Dashboard isAdmin={isAdmin} />} />
                                    <Route path="/batches" element={<BatchDashboard />} />
                                    <Route path="/latency" element={<Latency />} />
                                    {manualInvoiceCreatorEnabled && <Route path="/manual-invoice" element={<ManualInvoice />} />}
                                    {Configuration.featureFlags?.cfdiLookup === true && <Route path="/cfdi-lookup" element={<CfdiLookup />} />}
                                    <Route path="/failure-analysis" element={<FailureAnalysis />} />
                                    {isAdmin && <Route path="/reports" element={<Reports />} />}
                                    {isAdmin && <Route path="/chat" element={<ChatPage />} />}
                                </Routes>
                            </main>
                        </div>
                    </BrowserRouter>
                </div>
            </MsalAuthenticationTemplate>
        </FluentProvider>
    );
}

export default App;
